// src/pages/employeeCommission/components/ProductEmployeeLevelModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Search, Users } from 'lucide-react';
import { getUsers } from '../../../services/api/users';
import {
  getProductEmployeeConfigs,
  upsertCommissionConfig,
  deleteCommissionConfig,
} from '../../../services/api/employeeCommission';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './PageModals.module.css';

const EMPTY_FORM = {
  userId: '',
  userLabel: '',
  confirmCommission: '',
  createCommission: '',
  calculationBasis: 'order_count',
};

const ProductEmployeeLevelModal = ({ onClose, product }) => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const res = await getProductEmployeeConfigs(product.productId || product.id);
      setConfigs(res?.configs || []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async (val) => {
    setUserSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await getUsers({ search: val, limit: 30 });
        setUserOptions(res?.users || []);
      } catch { }
    }, 400);
  };

  const handleSaveNewRow = async () => {
    if (!form.userId) { showError(t('employeeCommission.selectEmployeeRequired')); return; }
    setSaving(true);
    try {
      await upsertCommissionConfig({
        userId: parseInt(form.userId),
        productId: product.productId || product.id,
        confirmCommission: parseFloat(form.confirmCommission) || 0,
        createCommission: parseFloat(form.createCommission) || 0,
        calculationBasis: form.calculationBasis,
      });
      success(t('employeeCommission.configSaved'));
      setForm(EMPTY_FORM);
      setUserSearch('');
      setUserOptions([]);
      setShowAddForm(false);
      await loadConfigs();
    } catch (err) {
      showError(err.response?.data?.message || t('employeeCommission.configSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    const confirmed = await confirm(t('employeeCommission.confirmDelete'));
    if (!confirmed) return;
    try {
      await deleteCommissionConfig(configId);
      success(t('employeeCommission.configDeleted'));
      await loadConfigs();
    } catch {
      showError(t('employeeCommission.configDeleteFailed'));
    }
  };

  const basisLabel = (key) => {
    const map = { order_count: t('employeeCommission.orderCount'), order_quantity: t('employeeCommission.orderQuantity'), order_value: t('employeeCommission.orderValue') };
    return map[key] || key;
  };

  const formatCommission = (config, field) => {
    const val = parseFloat(field === 'confirm' ? config.confirmCommission : config.createCommission).toFixed(2);
    return config.calculationBasis === 'order_value' ? `${val}%` : `LKR ${val}`;
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.modalLarge}`}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitle}>
              {t('employeeCommission.productEmployeeTitle').replace('{{name}}', product.name || product.displayName || '')}
            </h2>
            <p className={styles.modalSubtitle}>{t('employeeCommission.productEmployeeSubtitle')}</p>
          </div>
          <button className={styles.closeButton} onClick={onClose}><X size={16} /></button>
        </div>

        <div className={styles.modalBody}>
          {loading ? (
            <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center', padding: '2rem' }}>
              {t('employeeCommission.loading')}
            </p>
          ) : (
            <>
              {configs.length === 0 && !showAddForm ? (
                <div className={styles.emptyState}>
                  <Users size={40} />
                  <p>{t('employeeCommission.noProductEmployeeConfigs')}</p>
                </div>
              ) : (
                <table className={styles.innerTable}>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Emp #</th>
                      <th>Basis</th>
                      <th>Confirm</th>
                      <th>Create</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((c) => (
                      <tr key={c.id}>
                        <td>{c.employee?.name || '—'}</td>
                        <td style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8125rem' }}>
                          {c.employee?.employeeNumber || '—'}
                        </td>
                        <td>{basisLabel(c.calculationBasis)}</td>
                        <td>{formatCommission(c, 'confirm')}</td>
                        <td>{formatCommission(c, 'create')}</td>
                        <td>
                          <button className={styles.btnDanger} onClick={() => handleDelete(c.id)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {showAddForm && (
                <div className={styles.addRowForm} style={{ marginTop: '1rem' }}>
                  <div>
                    <div className={styles.searchWrapper}>
                      <Search size={14} className={styles.searchIcon} />
                      <input
                        className={styles.searchInput}
                        style={{ fontSize: '0.8125rem', padding: '0.5rem 0.75rem 0.5rem 2.25rem' }}
                        placeholder={t('employeeCommission.searchEmployee')}
                        value={userSearch}
                        onChange={(e) => handleUserSearch(e.target.value)}
                      />
                    </div>
                    {userOptions.length > 0 && (
                      <select
                        value={form.userId}
                        onChange={(e) => {
                          const u = userOptions.find((u) => String(u.id) === e.target.value);
                          setForm({ ...form, userId: e.target.value, userLabel: u?.name || '' });
                        }}
                        style={{ width: '100%', marginTop: '0.25rem' }}
                      >
                        <option value="">-- Select --</option>
                        {userOptions.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} {u.employeeNumber ? `(${u.employeeNumber})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {form.userLabel && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-success-dark)' }}>✓ {form.userLabel}</span>
                    )}
                  </div>
                  <select
                    value={form.calculationBasis}
                    onChange={(e) => setForm({ ...form, calculationBasis: e.target.value })}
                    style={{ padding: '0.5rem', border: '2px solid var(--color-gray-200)', borderRadius: 6, fontSize: '0.8125rem' }}
                  >
                    {['order_count', 'order_quantity', 'order_value'].map((v) => (
                      <option key={v} value={v}>{basisLabel(v)}</option>
                    ))}
                  </select>
                  <input
                    type="number" min="0" step="0.01" placeholder="Confirm"
                    value={form.confirmCommission}
                    onChange={(e) => setForm({ ...form, confirmCommission: e.target.value })}
                    style={{ padding: '0.5rem', border: '2px solid var(--color-gray-200)', borderRadius: 6, fontSize: '0.8125rem', width: '100%' }}
                  />
                  <input
                    type="number" min="0" step="0.01" placeholder="Create"
                    value={form.createCommission}
                    onChange={(e) => setForm({ ...form, createCommission: e.target.value })}
                    style={{ padding: '0.5rem', border: '2px solid var(--color-gray-200)', borderRadius: 6, fontSize: '0.8125rem', width: '100%' }}
                  />
                  <button className={styles.btnPrimary} style={{ padding: '0.5rem 0.875rem', fontSize: '0.8125rem' }} onClick={handleSaveNewRow} disabled={saving}>
                    {saving ? <div className={styles.spinner} /> : t('employeeCommission.add')}
                  </button>
                </div>
              )}

              <button
                className={styles.btnSecondary}
                style={{ marginTop: '1rem' }}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? <X size={14} /> : <Plus size={14} />}
                {showAddForm ? 'Cancel' : t('employeeCommission.addEmployeeOverride')}
              </button>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose}>{t('employeeCommission.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default ProductEmployeeLevelModal;